// ─── SEASONS ─────────────────────────────────────────────────────────────────────────────────────────────────
// The home page's season banner and its "Seasonal" category. The season is chosen from today's date; the picks are
// real products from the marketplace feed, scored against the season's keywords (name, description, the seller's
// own category) — generic words that fit almost any handmade product ("handmade", "set", "box") are deliberately not
// used — with a small boost for the product categories that suit the occasion. Nothing is invented: a product
// that doesn't fit isn't shown, and when too few fit, the closest categories fill the gap.
//
// Dates drift every year (Diwali moves), so the festive window is generous and the countdown only appears for years
// listed in DIWALI below — add the next year's date when it is announced.
export const DIWALI = { 2026: "2026-11-08", 2027: "2027-10-29", 2028: "2028-10-17" };

export const SEASONS = [
  {
    id: "festive", label: "Festive season", short: "Festive",
    from: [9, 20], to: [11, 20],            // 20 Sep → 20 Nov  (Navratri → Diwali → Bhai Dooj)
    eyebrow: "Festive season", title: "Light up the festive season", sub: "Gifts, jewellery and outfits from independent labels, in time for Diwali.",
    cta: "Shop festive picks",
    image: "/images/season-festive.jpg",
    credit: { text: "Photo: Siddarth Varanasi, CC BY 2.0", href: "https://commons.wikimedia.org/wiki/File:Diya_set_in_a_rangoli.jpg" },
    tint: "#FFC21F", base: "#FF9A2E",
    // Strong: the festival itself, and things that are unmistakably Indian/festive. Medium: gifting and family words,
    // Indian crafts/foods/fabrics, and jewellery & decor staples. (Words that fit any handmade product — "handmade",
    // "set", "box" — are deliberately absent.)
    strong: ["diwali", "deepavali", "festive", "festival", "diya", "diyas", "rangoli", "puja", "pooja", "lantern", "torana", "toran", "ethnic", "kurta", "kurti", "saree", "sari", "lehenga", "dupatta", "jhumka", "jhumkas", "hamper", "navratri", "dandiya", "kundan", "lakshmi", "ganesh", "thali",
      "indian", "india", "desi", "rakhi", "bhai dooj", "karwa chauth", "dhanteras", "mehendi", "mehndi", "henna", "bindi", "polki", "meenakari", "bandhani", "banarasi", "chikankari", "ikat", "madhubani", "kalamkari", "kantha", "handloom", "handwoven", "mithai", "namkeen", "kumkum", "sindoor", "paan", "temple jewellery"],
    medium: ["gift", "gifts", "gifting", "return gift", "combo", "family", "friends", "friend", "mom", "mother", "dad", "father", "sister", "brother", "wife", "husband", "couple", "birthday", "anniversary", "wedding", "bride", "bridal", "surprise", "personalised", "personalized", "customised", "customized", "keepsake", "souvenir",
      "lamp", "candle", "candles", "tea light", "tealight", "diffuser", "incense", "agarbatti", "bangle", "bangles", "necklace", "earring", "earrings", "choker", "anklet", "payal", "nath", "maang tikka", "traditional", "decor", "silk", "cotton", "embroidered", "embroidery", "oxidised", "oxidized", "gold", "golden", "jewellery", "jewelry", "block print", "zari", "brass", "terracotta", "clay", "wooden", "wall hanging", "toran", "sweet", "sweets", "spice", "spices", "masala", "chai", "tea", "ghee", "dry fruit", "dry fruits", "pickle", "gulkand"],
    cats: { bundles: 3, jewellery: 2, home: 2, apparel: 2, charms: 1, hair: 1, beauty: 1, bags: 1, collectibles: 1 },
  },
  {
    id: "winter", label: "Winter season", short: "Winter",
    from: [11, 21], to: [2, 29],
    eyebrow: "Winter season", title: "Warm things for cold days", sub: "Knits, layers and cosy objects from independent labels.",
    cta: "Shop winter picks",
    image: null, tint: "#BFD9F2", base: "#8EBBE6",
    strong: ["winter", "sweater", "knit", "knitted", "woollen", "woolen", "wool", "hoodie", "shawl", "scarf", "muffler", "jacket", "blanket", "cozy", "cosy", "thermal"],
    medium: ["warm", "candle", "candles", "mug", "gift", "beanie", "gloves", "socks", "fleece", "cardigan", "stole"],
    cats: { apparel: 1, home: 1, bundles: 1 },
  },
  {
    id: "summer", label: "Summer season", short: "Summer",
    from: [3, 1], to: [5, 31],
    eyebrow: "Summer season", title: "Light, bright and ready for the sun", sub: "Cotton, florals and easy pieces from independent labels.",
    cta: "Shop summer picks",
    image: null, tint: "#FFE27A", base: "#FFC93C",
    strong: ["summer", "cotton", "linen", "sunglasses", "sundress", "floral", "beach", "sandals", "straw", "hat", "tote"],
    medium: ["dress", "shorts", "top", "tee", "t-shirt", "bag", "sling", "earring", "earrings", "lightweight", "breathable", "pastel"],
    cats: { apparel: 1, bags: 1, jewellery: 1 },
  },
  {
    id: "monsoon", label: "Monsoon season", short: "Monsoon",
    from: [6, 1], to: [9, 19],
    eyebrow: "Monsoon season", title: "Made for the rains", sub: "Cosy corners, chai-time things and rain-ready picks.",
    cta: "Shop monsoon picks",
    image: null, tint: "#A8CDEB", base: "#6FA9D8",
    strong: ["monsoon", "rain", "raincoat", "umbrella", "waterproof", "chai", "cozy", "cosy"],
    medium: ["mug", "candle", "candles", "tote", "bag", "blanket", "journal", "book", "cotton", "gift"],
    cats: { home: 1, bags: 1, collectibles: 1 },
  },
];

const inWindow = (d, [fm, fd], [tm, td]) => {
  const v = (d.getMonth() + 1) * 100 + d.getDate(), a = fm * 100 + fd, b = tm * 100 + td;
  return a <= b ? v >= a && v <= b : v >= a || v <= b;   // windows like Nov 21 → Feb 10 wrap the year end
};

export function currentSeason(now = new Date()) {
  return SEASONS.find((s) => inWindow(now, s.from, s.to)) || SEASONS[0];
}

// Whole days from today to the year's Diwali (null when the date isn't listed, or it has passed).
export function daysToDiwali(now = new Date()) {
  const iso = DIWALI[now.getFullYear()];
  if (!iso) return null;
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const [y, m, d] = iso.split("-").map(Number);
  const days = Math.round((new Date(y, m - 1, d) - start) / 86400000);
  return days >= 0 ? days : null;
}

// Products the marketplace hasn't classified yet (taxonomy_cat empty) still need a category for the top-up, so it is
// inferred from the seller's own category label ("clothing", "home decor", "accessories" …).
const INFER = [
  ["jewellery", /jewel|earring|necklace|bangle|ring|accessor/], ["apparel", /cloth|apparel|fashion|wear|kurta|saree|dress|footwear|shoe/],
  ["home", /home|decor|kitchen|furnish|living/], ["bundles", /gift|hamper|bundle|combo|food|sweet|snack/], ["bags", /\bbag|purse|wallet/],
  ["beauty", /beauty|skin|makeup|cosmetic|perfume|fragrance/], ["hair", /hair/], ["charms", /charm|keychain|keyring/], ["collectibles", /collect|paper|stationer|art\b|print|book/],
];
export function categoryOf(p) {
  if (p.taxonomy_cat && p.taxonomy_cat !== "none") return p.taxonomy_cat;
  const label = String(p.seller_category || p.category || "").toLowerCase();
  const hit = INFER.find(([, re]) => re.test(label));
  return hit ? hit[0] : null;
}

const words = (text) => String(text || "").toLowerCase().replace(/[^a-z0-9\s-]/g, " ");
const hasTerm = (haystack, term) => (term.includes(" ") || term.includes("-") ? haystack.includes(term) : new RegExp(`(^|\\s)${term}(\\s|$)`).test(haystack));

export function seasonScore(p, season) {
  const hay = ` ${words(`${p.name || ""} ${p.description || ""} ${p.seller_category || ""} ${p.category || ""}`)} `.replace(/\s+/g, " ");
  let score = 0;
  for (const t of season.strong) if (hasTerm(hay, t)) score += 3;
  for (const t of season.medium) if (hasTerm(hay, t)) score += 2;
  return score;
}

// Real, buyable products that suit the season, best first. `isAvailable` lets the caller drop sold-out/paused items.
// Keyword matches come first (strongest first). The page is meant to be generous — a festive shopper wants a lot to
// browse — so when fewer than `min` match, the list is filled from the categories that suit the occasion (gifts,
// jewellery, home, apparel and the other gift-friendly ones), newest first, up to `max`.
export function seasonalProducts(products, season, { min = 60, max = 240, isAvailable = () => true } = {}) {
  const pool = (products || []).filter((p) => p && !p.hidden && isAvailable(p));
  const scored = pool.map((p) => ({ p, s: seasonScore(p, season), c: season.cats[categoryOf(p)] || 0 }));
  const direct = scored.filter((x) => x.s >= 2).sort((a, b) => (b.s + b.c) - (a.s + a.c)).map((x) => x.p);
  if (direct.length >= min) return direct.slice(0, max);
  const have = new Set(direct.map((p) => p.id));
  const created = (p) => Date.parse(p.created_at) || 0;
  const fill = scored.filter((x) => x.c > 0 && !have.has(x.p.id)).sort((a, b) => (b.c - a.c) || (created(b.p) - created(a.p))).map((x) => x.p);
  return [...direct, ...fill].slice(0, Math.max(min, direct.length)).slice(0, max);
}
